/**
 * SaveSystem — Spellroads save/load management.
 *
 * Storage: localStorage with lz-string compression.
 * Format: up to 3 named save slots + 1 auto-save slot.
 *
 * Each save snapshot includes:
 *   - Save metadata (timestamp, playtime, party summary)
 *   - WorldState (seed, position, fog, time, gold, rations, fame, quests)
 *   - CharacterSystem (all characters + their full state)
 *   - VehicleSystem vehicle list
 *   - CoopSystem player/character assignments
 *   - AchievementSystem progress
 *
 * Export/import: JSON file download/upload for debugging and portability.
 */

import LZString from 'lz-string';
import DebugLog from '../debug/DebugLog.js';

const SAVE_VERSION = 1;
const SLOT_PREFIX  = 'sr_save_';
const AUTO_SLOT    = 'auto';
const MAX_SLOTS    = 3;

const SaveSystem = {
  /** @type {boolean} Whether auto-save is enabled */
  autoSaveEnabled: true,

  /** @type {number} Auto-save interval in ms (5 minutes) */
  autoSaveInterval: 5 * 60 * 1000,

  /** @type {number|null} setInterval handle */
  _autoSaveTimer: null,

  // ── Init ────────────────────────────────────────────────────────────────────

  init() {
    DebugLog.info('[save] SaveSystem initialized');
  },

  /** Start the auto-save timer. Call after world is ready. */
  startAutoSave() {
    this.stopAutoSave();
    if (!this.autoSaveEnabled) return;
    this._autoSaveTimer = setInterval(() => {
      this.save(AUTO_SLOT).catch(err =>
        DebugLog.error(`[save] Auto-save failed: ${err.message}`)
      );
    }, this.autoSaveInterval);
    DebugLog.info('[save] Auto-save started');
  },

  stopAutoSave() {
    if (this._autoSaveTimer !== null) {
      clearInterval(this._autoSaveTimer);
      this._autoSaveTimer = null;
    }
  },

  // ── Save ─────────────────────────────────────────────────────────────────────

  /**
   * Serialize and save the current game state to a named slot.
   * @param {string} [slot='auto'] — 'auto', '1', '2', or '3'
   * @returns {Promise<{ ok: boolean, message: string }>}
   */
  async save(slot = AUTO_SLOT) {
    try {
      const snapshot = this._buildSnapshot();
      const json     = JSON.stringify(snapshot);
      const compressed = LZString.compressToUTF16(json);
      const key = SLOT_PREFIX + slot;

      localStorage.setItem(key, compressed);

      DebugLog.info(`[save] Saved to slot "${slot}" — ${Math.round(json.length / 1024)}KB raw, ${Math.round(compressed.length / 512)}KB compressed`);
      return { ok: true, message: `Game saved to slot ${slot}` };
    } catch (err) {
      DebugLog.error(`[save] Save failed: ${err.message}`);
      return { ok: false, message: `Save failed: ${err.message}` };
    }
  },

  /**
   * Quicksave to slot 1.
   */
  async quickSave() {
    return this.save('1');
  },

  // ── Load ─────────────────────────────────────────────────────────────────────

  /**
   * Load a saved game state from a slot and restore all systems.
   * @param {string} [slot='auto']
   * @returns {Promise<{ ok: boolean, message: string }>}
   */
  async load(slot = AUTO_SLOT) {
    try {
      const key = SLOT_PREFIX + slot;
      const compressed = localStorage.getItem(key);
      if (!compressed) {
        return { ok: false, message: `No save data in slot "${slot}"` };
      }

      const json = LZString.decompressFromUTF16(compressed);
      if (!json) {
        return { ok: false, message: `Save data in slot "${slot}" is corrupted` };
      }

      const snapshot = JSON.parse(json);

      if (snapshot.version !== SAVE_VERSION) {
        DebugLog.warn(`[save] Save version mismatch: got ${snapshot.version}, expected ${SAVE_VERSION}`);
      }

      await this._restoreSnapshot(snapshot);

      DebugLog.info(`[save] Loaded slot "${slot}" — saved at ${new Date(snapshot.savedAt).toLocaleString()}`);
      return { ok: true, message: `Game loaded from slot ${slot}` };
    } catch (err) {
      DebugLog.error(`[save] Load failed: ${err.message}`);
      return { ok: false, message: `Load failed: ${err.message}` };
    }
  },

  /**
   * Quickload from slot 1.
   */
  async quickLoad() {
    return this.load('1');
  },

  // ── Slot info ─────────────────────────────────────────────────────────────────

  /**
   * Get metadata for all save slots without fully loading them.
   * @returns {object[]} Array of { slot, empty, meta } objects
   */
  getSlotInfo() {
    const slots = [AUTO_SLOT, '1', '2', '3'];
    return slots.map(slot => {
      const key = SLOT_PREFIX + slot;
      const compressed = localStorage.getItem(key);
      if (!compressed) return { slot, empty: true, meta: null };

      try {
        const json = LZString.decompressFromUTF16(compressed);
        const snapshot = JSON.parse(json);
        return { slot, empty: false, meta: snapshot.meta ?? null };
      } catch {
        return { slot, empty: false, meta: null, corrupted: true };
      }
    });
  },

  /** Delete a save slot. */
  deleteSlot(slot) {
    localStorage.removeItem(SLOT_PREFIX + slot);
    DebugLog.info(`[save] Deleted slot "${slot}"`);
  },

  /** Returns true if any non-auto slot has save data. */
  hasSave() {
    return ['auto', '1', '2', '3'].some(s => !!localStorage.getItem(SLOT_PREFIX + s));
  },

  // ── Export / Import ──────────────────────────────────────────────────────────

  /**
   * Export the current game state as a JSON file download.
   * Triggers a browser file download.
   */
  exportToFile() {
    const snapshot = this._buildSnapshot();
    const json = JSON.stringify(snapshot, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `spellroads_save_${Date.now()}.json`;
    a.click();

    URL.revokeObjectURL(url);
    DebugLog.info('[save] Exported save to file');
  },

  /**
   * Import a save from a JSON file (triggers file picker).
   * @returns {Promise<{ ok: boolean, message: string }>}
   */
  importFromFile() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';

      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return resolve({ ok: false, message: 'No file selected' });

        try {
          const text = await file.text();
          const snapshot = JSON.parse(text);
          await this._restoreSnapshot(snapshot);
          resolve({ ok: true, message: 'Save imported successfully' });
        } catch (err) {
          resolve({ ok: false, message: `Import failed: ${err.message}` });
        }
      };

      input.click();
    });
  },

  // ── Snapshot builder ─────────────────────────────────────────────────────────

  _buildSnapshot() {
    // Lazy-import systems to avoid circular deps — use synchronous window globals
    const WorldState       = window.__worldState       ?? this._tryImportWorldState();
    const CharSystem       = window.__characterSystem  ?? null;
    const VehicleSystem    = window.__vehicleSystem    ?? null;
    const CoopSystem       = window.__coopSystem       ?? null;
    const AchievSystem     = window.__achievementSystem ?? null;
    const PrestigeSystem   = window.__prestigeSystem   ?? null;
    const WorldEventSystem = window.__worldEventSystem ?? null;

    // Characters
    const characters = CharSystem
      ? (CharSystem.getAll?.() ?? []).map(c => CharSystem.serialize?.(c) ?? c)
      : [];

    // Fog of war — convert Map to array of entries for JSON serialization
    const fogEntries = WorldState?.fog
      ? Array.from(WorldState.fog.entries())
      : [];

    // Party summary for slot UI display
    const heroes = characters.filter(c => c.isHero);
    const meta = {
      savedAt:      Date.now(),
      partyNames:   heroes.map(c => c.name),
      partyLevels:  heroes.map(c => c.level ?? 1),
      partyClasses: heroes.map(c => c.class ?? ''),
      regionName:   WorldState?.world?.regions?.find(r => r.id === WorldState.currentRegionId)?.name ?? 'Unknown',
      regionLevel:  WorldState?.currentRegionLevel ?? 1,
      playtime:     WorldState?._playtimeSeconds ?? 0,
    };

    return {
      version: SAVE_VERSION,
      savedAt: Date.now(),
      meta,
      world: WorldState ? {
        seed:              WorldState.world?.seed ?? null,
        worldData:         WorldState.world,
        currentRegionId:   WorldState.currentRegionId,
        currentLocalMapId: WorldState.currentLocalMapId,
        partyPosition:     WorldState.partyPosition,
        time:              WorldState.time,
        partyFame:         WorldState.partyFame,
        partyGold:         WorldState.partyGold,
        rationPoints:      WorldState.rationPoints,
        campEquipment:     WorldState.campEquipment,
        activeQuests:      WorldState.activeQuests ?? [],
        completedQuests:   WorldState.completedQuests ?? [],
        fog:               fogEntries,
        playerCount:       WorldState.playerCount ?? 1,
        currentBiome:      WorldState.currentBiome,
        currentRegionLevel: WorldState.currentRegionLevel,
        dungeonState:      WorldState.dungeonState ?? null,
      } : null,
      characters,
      vehicles: VehicleSystem?.getAll?.() ?? [],
      coopAssignments: CoopSystem?.getState?.()?.assignments ?? {},
      achievements: AchievSystem?.getProgress?.() ?? {},
      prestige: PrestigeSystem?.serialize?.() ?? { prestige: 0, bonuses: [] },
      worldEvents: WorldEventSystem?.getState?.() ?? { activeEvents: [], eventHistory: [], cooldowns: [] },
    };
  },

  _tryImportWorldState() {
    // Fallback: try to get WorldState from global (set during world init)
    return null;
  },

  async _restoreSnapshot(snapshot) {
    if (!snapshot?.world) {
      throw new Error('Snapshot has no world data');
    }

    const WorldState       = window.__worldState       ?? null;
    const CharSystem       = window.__characterSystem  ?? null;
    const VehicleSystem    = window.__vehicleSystem    ?? null;
    const CoopSystem       = window.__coopSystem       ?? null;
    const AchievSystem     = window.__achievementSystem ?? null;
    const PrestigeSystem   = window.__prestigeSystem   ?? null;
    const WorldEventSystem = window.__worldEventSystem ?? null;

    // Restore world state
    if (WorldState && snapshot.world) {
      const w = snapshot.world;
      WorldState.world             = w.worldData;
      WorldState.currentRegionId   = w.currentRegionId;
      WorldState.currentLocalMapId = w.currentLocalMapId;
      WorldState.partyPosition     = w.partyPosition ?? { x: 20, y: 15 };
      WorldState.time              = w.time ?? { day: 1, hour: 8, minute: 0 };
      WorldState.partyFame         = w.partyFame ?? 0;
      WorldState.partyGold         = w.partyGold ?? 200;
      WorldState.rationPoints      = w.rationPoints ?? 20;
      WorldState.campEquipment     = w.campEquipment ?? { hasTent: false, hasAlarmWire: false, hasPalisade: false };
      WorldState.activeQuests      = w.activeQuests ?? [];
      WorldState.completedQuests   = w.completedQuests ?? [];
      WorldState.playerCount       = w.playerCount ?? 1;
      WorldState.currentBiome      = w.currentBiome ?? 'forest';
      WorldState.currentRegionLevel = w.currentRegionLevel ?? 1;
      WorldState.dungeonState      = w.dungeonState ?? null;

      // Restore fog Map from array entries
      WorldState.fog = new Map(w.fog ?? []);
    }

    // Restore characters
    if (CharSystem && snapshot.characters?.length > 0) {
      CharSystem._characters = {};
      for (const charData of snapshot.characters) {
        const character = CharSystem.deserialize(charData);
        CharSystem._characters[character.id] = character;
      }
    }

    // Restore vehicles
    if (VehicleSystem && snapshot.vehicles?.length > 0) {
      VehicleSystem._vehicles = snapshot.vehicles;
    }

    // Restore coop assignments
    if (CoopSystem && snapshot.coopAssignments) {
      CoopSystem._assignments = snapshot.coopAssignments;
    }

    // Restore achievements
    if (AchievSystem && snapshot.achievements) {
      AchievSystem.restoreProgress(snapshot.achievements);
    }

    // Restore prestige state
    if (PrestigeSystem && snapshot.prestige) {
      PrestigeSystem.restore(snapshot.prestige);
    }

    // Restore world event state
    if (WorldEventSystem && snapshot.worldEvents) {
      WorldEventSystem.restoreState(snapshot.worldEvents);
    }

    DebugLog.info('[save] Snapshot restored successfully');
  },
};

export default SaveSystem;
